import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Eye, IndianRupee, Upload, Download, Edit, Search, Send, XCircle, RefreshCw, RotateCcw, Undo2, Ban, Trash2, X, Calendar, TrendingUp, Wallet, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoicesManager() {
  const { admin, getAuthHeader } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [cancelledInvoices, setCancelledInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'cancelled'
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [recallInvoice, setRecallInvoice] = useState(null);
  const [recallNotes, setRecallNotes] = useState('');
  const [cancelData, setCancelData] = useState({ reason: '', notes: '' });
  const [cancellingInvoice, setCancellingInvoice] = useState(false);
  const [recallingInvoice, setRecallingInvoice] = useState(null);
  const [cancellingPayment, setCancellingPayment] = useState(null);
  const [deletingPayment, setDeletingPayment] = useState(null);
  const [refundData, setRefundData] = useState({ 
    refund_type: 'full', 
    refund_amount: '', 
    refund_method: 'Bank Transfer',
    refund_reference: '',
    notes: '' 
  });
  const [processingRefund, setProcessingRefund] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer',
    transaction_reference: '',
    notes: '',
    screenshot_files: []
  });
  const [paymentScreenshots, setPaymentScreenshots] = useState([]);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  // Filter states
  const [leadIdFilter, setLeadIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // 'tax' or 'cash' or ''
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState('all');

  // Calculate stats based on filtered invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // Date filter
      if (dateFrom || dateTo) {
        const invDate = inv.created_at ? new Date(inv.created_at).toISOString().split('T')[0] : null;
        if (dateFrom && invDate && invDate < dateFrom) return false;
        if (dateTo && invDate && invDate > dateTo) return false;
      }
      return true;
    });
  }, [invoices, dateFrom, dateTo]);

  // Apply all filters (date + lead ID + status + type)
  const displayInvoices = useMemo(() => {
    return filteredInvoices.filter(invoice => {
      if (leadIdFilter && !invoice.lead_id?.toLowerCase().includes(leadIdFilter.toLowerCase())) {
        return false;
      }
      if (statusFilter && invoice.payment_status !== statusFilter) {
        return false;
      }
      if (typeFilter && invoice.invoice_type !== typeFilter) {
        return false;
      }
      return true;
    });
  }, [filteredInvoices, leadIdFilter, statusFilter, typeFilter]);

  // Stats based on all applied filters
  const stats = useMemo(() => {
    const totalSales = displayInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    const totalCollected = displayInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
    const totalDue = displayInvoices.reduce((sum, inv) => sum + (inv.amount_due || 0), 0);
    return {
      totalSales: Math.round(totalSales),
      totalCollected: Math.round(totalCollected),
      totalDue: Math.round(totalDue),
      count: displayInvoices.length
    };
  }, [displayInvoices]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchInvoices(), fetchCancelledInvoices()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchCancelledInvoices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/cancelled`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setCancelledInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching cancelled invoices:', error);
    }
  };

  // Native fetch to bypass rrweb-recorder wrapper
  const nativeFetch = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      
      // Set headers
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ ok: true, data, status: xhr.status });
          } catch (e) {
            resolve({ ok: true, data: xhr.responseText, status: xhr.status });
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject({ ok: false, error, status: xhr.status });
          } catch (e) {
            reject({ ok: false, error: { detail: xhr.statusText }, status: xhr.status });
          }
        }
      };
      
      xhr.onerror = function() {
        reject({ ok: false, error: { detail: 'Network error' }, status: 0 });
      };
      
      xhr.send(options.body || null);
    });
  };

  const handleSendInvoice = async (invoiceId, invoiceNumber) => {
    const confirmed = await window.confirm(`Send invoice ${invoiceNumber} to the client via email?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await nativeFetch(`${BACKEND_URL}/api/invoices/${invoiceId}/send`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (response.ok) {
        alert(response.data.message || 'Invoice sent successfully');
        fetchInvoices(); // Refresh to update sent status
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert(error.error?.detail || 'Failed to send invoice');
    }
  };

  const handleSendPaymentReceipt = async (invoiceId, invoiceNumber, paymentIndex) => {
    const confirmed = await window.confirm(`Send payment receipt for invoice ${invoiceNumber} to the client via email?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await nativeFetch(`${BACKEND_URL}/api/invoices/${invoiceId}/payment-receipt/${paymentIndex}/send`, {
        method: 'POST',
        headers: getAuthHeader()
      });

      if (response.ok) {
        alert(response.data.message || 'Payment receipt sent successfully');
        fetchInvoices(); // Refresh to update sent status
      }
    } catch (error) {
      console.error('Error sending payment receipt:', error);
      alert(error.error?.detail || 'Failed to send payment receipt');
    }
  };

  const handlePaymentScreenshotUpload = async (files) => {
    const uploadedUrls = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/invoices/${selectedInvoice.id}/upload-payment-screenshot`,
          {
            method: 'POST',
            headers: getAuthHeader(),
            body: formData
          }
        );

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.screenshot_url);
        }
      } catch (error) {
        console.error('Error uploading screenshot:', error);
      }
    }

    return uploadedUrls;
  };

  const handleConfirmPayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    setSubmittingPayment(true);
    try {
      // Upload screenshots first
      let screenshotUrls = [];
      if (paymentScreenshots.length > 0) {
        screenshotUrls = await handlePaymentScreenshotUpload(paymentScreenshots);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${selectedInvoice.id}/confirm-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            amount: parseFloat(paymentData.amount),
            payment_date: paymentData.payment_date,
            payment_method: paymentData.payment_method,
            transaction_reference: paymentData.transaction_reference,
            notes: paymentData.notes,
            screenshot_files: screenshotUrls
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowPaymentModal(false);
        setSelectedInvoice(null);
        setPaymentData({
          amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          payment_method: 'Bank Transfer',
          transaction_reference: '',
          notes: '',
          screenshot_files: []
        });
        setPaymentScreenshots([]);
        fetchInvoices();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to confirm payment');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('Failed to confirm payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'partial': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-red-100 text-red-800';
    }
  };

  const handleCancelInvoice = async () => {
    if (!cancelData.reason.trim()) {
      alert('Please provide a cancellation reason');
      return;
    }

    setCancellingInvoice(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${selectedInvoice.id}/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            reason: cancelData.reason,
            notes: cancelData.notes
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowCancelModal(false);
        setSelectedInvoice(null);
        setCancelData({ reason: '', notes: '' });
        fetchInvoices();
        fetchCancelledInvoices();
        setActiveTab('cancelled'); // Switch to cancelled tab
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel invoice');
      }
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      alert('Failed to cancel invoice');
    } finally {
      setCancellingInvoice(false);
    }
  };

  // Recall a cancelled invoice (only if no refund initiated)
  const handleRecallInvoice = async () => {
    if (!recallInvoice) return;
    
    setRecallingInvoice(recallInvoice.id);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${recallInvoice.id}/recall`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({ notes: recallNotes })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowRecallModal(false);
        setRecallInvoice(null);
        setRecallNotes('');
        await loadAllData();
        setActiveTab('active'); // Switch to active tab after recall
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to recall invoice');
      }
    } catch (error) {
      console.error('Error recalling invoice:', error);
      alert('Failed to recall invoice');
    } finally {
      setRecallingInvoice(null);
    }
  };

  // State for cancel payment modal
  const [showCancelPaymentModal, setShowCancelPaymentModal] = useState(false);
  const [cancelPaymentInvoiceId, setCancelPaymentInvoiceId] = useState(null);
  const [cancelPaymentIndex, setCancelPaymentIndex] = useState(null);
  const [cancelPaymentAmount, setCancelPaymentAmount] = useState(0);
  const [cancelPaymentReason, setCancelPaymentReason] = useState('');

  // Open cancel payment modal
  const openCancelPaymentModal = (invoiceId, paymentIndex, paymentAmount) => {
    setCancelPaymentInvoiceId(invoiceId);
    setCancelPaymentIndex(paymentIndex);
    setCancelPaymentAmount(paymentAmount);
    setCancelPaymentReason('');
    setShowCancelPaymentModal(true);
  };

  // Cancel a payment record
  const handleCancelPayment = async () => {
    if (!cancelPaymentReason.trim()) {
      alert('Please enter a reason for cancellation');
      return;
    }
    
    setCancellingPayment(`${cancelPaymentInvoiceId}-${cancelPaymentIndex}`);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${cancelPaymentInvoiceId}/payment/${cancelPaymentIndex}/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({ reason: cancelPaymentReason })
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowCancelPaymentModal(false);
        setCancelPaymentInvoiceId(null);
        setCancelPaymentIndex(null);
        setCancelPaymentReason('');
        await loadAllData();
        // Refresh selected invoice if viewing it
        if (selectedInvoice?.id === cancelPaymentInvoiceId) {
          const response = await fetch(`${BACKEND_URL}/api/invoices?lead_id=${selectedInvoice.lead_id}`, {
            headers: getAuthHeader()
          });
          if (response.ok) {
            const data = await response.json();
            const updatedInvoice = data.find(inv => inv.id === cancelPaymentInvoiceId);
            if (updatedInvoice) setSelectedInvoice(updatedInvoice);
          }
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel payment');
      }
    } catch (error) {
      console.error('Error cancelling payment:', error);
      alert('Failed to cancel payment');
    } finally {
      setCancellingPayment(null);
    }
  };

  // Delete a payment record (SuperAdmin only)
  const handleDeletePayment = async (invoiceId, paymentIndex, paymentAmount) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE this payment of ₹${Math.round(paymentAmount).toLocaleString()}? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingPayment(`${invoiceId}-${paymentIndex}`);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${invoiceId}/payment/${paymentIndex}`,
        {
          method: 'DELETE',
          headers: getAuthHeader()
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        await loadAllData();
        // Refresh selected invoice if viewing it
        if (selectedInvoice?.id === invoiceId) {
          const updatedInvoice = invoices.find(inv => inv.id === invoiceId);
          if (updatedInvoice) setSelectedInvoice(updatedInvoice);
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment');
    } finally {
      setDeletingPayment(null);
    }
  };

  const handleCreateRefund = async () => {
    if (refundData.refund_type === 'partial' && (!refundData.refund_amount || parseFloat(refundData.refund_amount) <= 0)) {
      alert('Please enter a valid refund amount');
      return;
    }

    setProcessingRefund(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/invoices/${selectedInvoice.id}/refund`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          },
          body: JSON.stringify({
            refund_type: refundData.refund_type,
            refund_amount: refundData.refund_type === 'partial' ? parseFloat(refundData.refund_amount) : null,
            refund_method: refundData.refund_method,
            refund_reference: refundData.refund_reference,
            notes: refundData.notes
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        let message = result.message;
        
        // Show commission impact details
        if (result.commission_impact && result.commission_impact.length > 0) {
          message += '\n\nCommission Impact:';
          result.commission_impact.forEach(impact => {
            if (impact.action === 'reversal_created') {
              message += `\n• ${impact.partner_name}: Reversal of ₹${impact.amount_to_recover} created for recovery`;
            } else if (impact.action === 'voided') {
              message += `\n• ${impact.partner_name}: Commission of ₹${impact.amount_adjusted} voided`;
            } else {
              message += `\n• ${impact.partner_name}: Commission reduced by ₹${impact.amount_adjusted}`;
            }
          });
        }
        
        alert(message);
        setShowRefundModal(false);
        setSelectedInvoice(null);
        setRefundData({ refund_type: 'full', refund_amount: '', refund_method: 'Bank Transfer', refund_reference: '', notes: '' });
        fetchCancelledInvoices();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create refund');
      }
    } catch (error) {
      console.error('Error creating refund:', error);
      alert('Failed to create refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  // Quick date filter presets
  const setDatePreset = (preset) => {
    setActiveDatePreset(preset);
    const today = new Date();
    let from = '';
    let to = today.toISOString().split('T')[0];
    
    switch(preset) {
      case 'today':
        from = to;
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        from = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        from = monthAgo.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterAgo = new Date(today);
        quarterAgo.setMonth(today.getMonth() - 3);
        from = quarterAgo.toISOString().split('T')[0];
        break;
      case 'year':
        const yearAgo = new Date(today);
        yearAgo.setFullYear(today.getFullYear() - 1);
        from = yearAgo.toISOString().split('T')[0];
        break;
      case 'all':
        from = '';
        to = '';
        break;
      default:
        break;
    }
    setDateFrom(from);
    setDateTo(to);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading invoices...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Invoices Management</h1>
          <p className="text-gray-600 text-sm">View and manage all invoices</p>
        </div>

        {/* Compact Stat Cards + Date Filter Row */}
        <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="invoice-stats-row">
          {/* Stat Cards */}
          <div className="flex gap-3 flex-wrap" data-testid="invoice-stat-cards">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-3 shadow-sm" data-testid="stat-card-total-sales">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <TrendingUp size={16} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Sales</p>
                <p className="text-sm font-bold text-gray-800" data-testid="stat-total-sales-value">₹{stats.totalSales.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-3 shadow-sm" data-testid="stat-card-collected">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <Wallet size={16} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Collected</p>
                <p className="text-sm font-bold text-green-600" data-testid="stat-collected-value">₹{stats.totalCollected.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-3 shadow-sm" data-testid="stat-card-due">
              <div className="p-1.5 bg-red-100 rounded-lg">
                <CreditCard size={16} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Due</p>
                <p className="text-sm font-bold text-red-600" data-testid="stat-due-value">₹{stats.totalDue.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2 ml-auto flex-wrap" data-testid="date-filter-container">
            {/* Type Filter - Tax/Cash */}
            <div className="flex gap-1" data-testid="type-filter-buttons">
              {[
                { label: 'All', value: '' },
                { label: 'Tax', value: 'tax' },
                { label: 'Cash', value: 'cash' }
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setTypeFilter(type.value)}
                  data-testid={`type-filter-${type.value || 'all'}`}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    typeFilter === type.value
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            <span className="text-gray-300">|</span>
            {/* Date Presets */}
            <div className="flex gap-1" data-testid="date-preset-buttons">
              {[
                { label: 'Today', value: 'today' },
                { label: '7D', value: 'week' },
                { label: '1M', value: 'month' },
                { label: '3M', value: 'quarter' },
                { label: 'All', value: 'all' }
              ].map(preset => (
                <button
                  key={preset.value}
                  onClick={() => setDatePreset(preset.value)}
                  data-testid={`date-preset-${preset.value}`}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    activeDatePreset === preset.value
                      ? 'bg-purple-50 border-purple-300 text-purple-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setActiveDatePreset('custom');
                }}
                className="border border-gray-300 rounded px-2 py-1 text-xs w-28"
                placeholder="From"
                data-testid="date-filter-from"
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setActiveDatePreset('custom');
                }}
                className="border border-gray-300 rounded px-2 py-1 text-xs w-28"
                placeholder="To"
                data-testid="date-filter-to"
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Invoices ({displayInvoices.length})
            </button>
            <button
              onClick={() => setActiveTab('cancelled')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cancelled'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cancelled Invoices ({cancelledInvoices.length})
            </button>
          </nav>
        </div>

        {/* Active Invoices Tab */}
        {activeTab === 'active' && (
          <>
            {/* Filters */}
            <div className="mb-6 bg-white rounded-lg shadow-md p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search by Lead ID
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Enter Lead ID..."
                      value={leadIdFilter}
                      onChange={(e) => setLeadIdFilter(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Status</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setLeadIdFilter('');
                      setStatusFilter('');
                      setTypeFilter('');
                      setDateFrom('');
                      setDateTo('');
                      setActiveDatePreset('all');
                    }}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    data-testid="clear-filters-btn"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {displayInvoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="no-invoices-message">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {invoices.length === 0 ? 'No invoices found' : 'No invoices match your filters'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="invoices-table-container">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="active-invoices-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {displayInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50" data-testid={`invoice-row-${invoice.id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-purple-600">
                      {invoice.lead_id}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.invoice_type === 'tax' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {invoice.invoice_type === 'tax' ? 'TAX' : 'CASH'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{invoice.lead_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                      ₹{invoice.total_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-green-600 font-semibold">
                      ₹{invoice.amount_paid.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600 font-semibold">
                      ₹{invoice.amount_due.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.payment_status)}`}>
                        {invoice.payment_status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/invoices/${invoice.id}/download-pdf`, {
                                headers: getAuthHeader()
                              });
                              
                              if (response.ok) {
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Invoice_${invoice.invoice_number.replace(/\//g, '_')}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } else {
                                alert('Failed to download invoice PDF');
                              }
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Failed to download invoice PDF');
                            }
                          }}
                          className="text-purple-600 hover:text-purple-800"
                          title="Download PDF"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => handleSendInvoice(invoice.id, invoice.invoice_number)}
                          className="text-blue-600 hover:text-blue-800"
                          title={invoice.email_sent ? "Re-Send Invoice" : "Send Invoice"}
                        >
                          <Send size={18} />
                          {invoice.email_sent && <span className="text-xs ml-1">Re-Send</span>}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPaymentModal(true);
                            setPaymentData({ ...paymentData, amount: invoice.amount_due });
                          }}
                          className="text-green-600 hover:text-green-800"
                          title="View Details"
                        >
                          <Eye size={18} />
                        </button>
                        {invoice.payment_status !== 'paid' && (
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowPaymentModal(true);
                              setPaymentData({ ...paymentData, amount: invoice.amount_due });
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Record Payment"
                          >
                            <IndianRupee size={18} />
                          </button>
                        )}
                        {invoice.payment_status !== 'cancelled' && admin?.role === 'super_admin' && (
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowCancelModal(true);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Cancel Invoice"
                          >
                            <XCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
        </>
        )}

        {/* Cancelled Invoices Tab */}
        {activeTab === 'cancelled' && (
          <>
            {cancelledInvoices.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <XCircle size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No cancelled invoices found</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Paid</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cancellation Reason</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cancelled At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Refund Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {cancelledInvoices.map(invoice => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-purple-600">
                            {invoice.lead_id}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {invoice.invoice_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{invoice.lead_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-semibold">
                            ₹{Math.round(invoice.amount_paid || 0).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={invoice.cancellation?.cancellation_reason}>
                            {invoice.cancellation?.cancellation_reason || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {invoice.cancellation?.cancelled_at 
                              ? new Date(invoice.cancellation.cancelled_at).toLocaleString('en-IN')
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            {invoice.refund ? (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Refunded ₹{Math.round(invoice.refund.refund_amount || 0).toLocaleString()}
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Pending Refund
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Recall button - only if no refund initiated */}
                              {!invoice.refund && (admin?.role === 'super_admin' || admin?.role === 'admin') && (
                                <button
                                  onClick={() => {
                                    setRecallInvoice(invoice);
                                    setRecallNotes('');
                                    setShowRecallModal(true);
                                  }}
                                  disabled={recallingInvoice === invoice.id}
                                  className="px-3 py-1 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 flex items-center gap-1 disabled:opacity-50"
                                  title="Recall Invoice (Undo Cancellation)"
                                >
                                  <Undo2 size={14} />
                                  {recallingInvoice === invoice.id ? 'Recalling...' : 'Recall'}
                                </button>
                              )}
                              {/* Create Refund button */}
                              {!invoice.refund && invoice.amount_paid > 0 && admin?.role === 'super_admin' && (
                                <button
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setRefundData({
                                      ...refundData,
                                      refund_amount: invoice.amount_paid
                                    });
                                    setShowRefundModal(true);
                                  }}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1"
                                  title="Create Refund"
                                >
                                  <RotateCcw size={14} />
                                  Refund
                                </button>
                              )}
                              {invoice.refund && (
                                <span className="text-xs text-gray-500">
                                  {invoice.refund.refund_type === 'full' ? 'Full' : 'Partial'} refund via {invoice.refund.refund_method}
                                </span>
                              )}
                              {!invoice.refund && invoice.amount_paid === 0 && (
                                <span className="text-xs text-gray-400 italic">No payment recorded</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                Invoice {selectedInvoice.invoice_number}
              </h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedInvoice(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Invoice Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Client Information</h3>
                <div className="bg-blue-50 p-3 rounded-lg mb-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-semibold">{selectedInvoice.lead_name}</span>
                    </div>
                    {selectedInvoice.lead_phone && (
                      <div>
                        <span className="text-gray-600">Phone:</span>
                        <span className="ml-2">{selectedInvoice.lead_phone}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2">{selectedInvoice.lead_email}</span>
                    </div>
                    {selectedInvoice.service_type && (
                      <div>
                        <span className="text-gray-600">Service:</span>
                        <span className="ml-2">{selectedInvoice.service_type}</span>
                      </div>
                    )}
                    {selectedInvoice.destination_country && (
                      <div>
                        <span className="text-gray-600">Destination:</span>
                        <span className="ml-2">{selectedInvoice.destination_country}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Invoice Type:</span>
                    <span className="ml-2 font-semibold">{selectedInvoice.invoice_type.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Issue Date:</span>
                    <span className="ml-2">{new Date(selectedInvoice.issue_date).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Status:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedInvoice.payment_status)}`}>
                      {selectedInvoice.payment_status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-semibold">₹{Math.round(selectedInvoice.subtotal || 0).toLocaleString()}</span>
                  </div>
                  {selectedInvoice.tax_breakdown && (
                    <>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>CGST ({selectedInvoice.tax_breakdown.cgst_rate}%):</span>
                        <span>₹{Math.round(selectedInvoice.tax_breakdown.cgst_amount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>SGST ({selectedInvoice.tax_breakdown.sgst_rate}%):</span>
                        <span>₹{Math.round(selectedInvoice.tax_breakdown.sgst_amount || 0).toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-blue-200">
                    <span>Total Amount:</span>
                    <span>₹{Math.round(selectedInvoice.total_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Amount Paid:</span>
                    <span className="font-semibold">₹{Math.round(selectedInvoice.amount_paid || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-red-600 text-lg font-bold">
                    <span>Amount Due:</span>
                    <span>₹{Math.round(selectedInvoice.amount_due || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment Records */}
              {selectedInvoice.payment_records && selectedInvoice.payment_records.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Payment History</h3>
                  <div className="space-y-3">
                    {selectedInvoice.payment_records.map((record, index) => (
                      <div 
                        key={index} 
                        className={`border rounded-lg p-3 text-sm ${
                          record.status === 'cancelled' 
                            ? 'border-red-200 bg-red-50 opacity-80' 
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold ${record.status === 'cancelled' ? 'line-through text-gray-500' : ''}`}>
                                ₹{Math.round(record.amount || 0).toLocaleString()}
                              </span>
                              {record.status === 'cancelled' && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">
                                  CANCELLED
                                </span>
                              )}
                              <span className="text-gray-500 text-xs ml-auto">
                                {new Date(record.payment_date).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-gray-600 text-xs">
                              {record.payment_method} {record.transaction_reference && `- ${record.transaction_reference}`}
                            </div>
                            {record.notes && (
                              <div className="text-gray-500 text-xs mt-1">{record.notes}</div>
                            )}
                            {record.recorded_by && (
                              <div className="text-gray-400 text-xs mt-1">
                                Recorded by {record.recorded_by}
                              </div>
                            )}
                            {/* Cancellation info */}
                            {record.status === 'cancelled' && (
                              <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                                <strong>Cancelled</strong> by {record.cancelled_by} 
                                {record.cancelled_at && ` on ${new Date(record.cancelled_at).toLocaleString()}`}
                                {record.cancel_reason && <div>Reason: {record.cancel_reason}</div>}
                              </div>
                            )}
                            {record.screenshot_urls && record.screenshot_urls.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs font-medium text-gray-700 mb-1">Payment Proof:</div>
                                <div className="flex flex-wrap gap-2">
                                  {record.screenshot_urls.map((url, idx) => (
                                    <a
                                      key={idx}
                                      href={`${BACKEND_URL}${url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 hover:bg-blue-100 transition-colors"
                                      title="View payment proof screenshot"
                                    >
                                      <Eye size={12} />
                                      Screenshot {idx + 1}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            {/* Receipt buttons - only for active payments */}
                            {record.status !== 'cancelled' && (
                              <>
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(
                                        `${BACKEND_URL}/api/invoices/${selectedInvoice.id}/payment-receipt/${index}/download-pdf`,
                                        { headers: getAuthHeader() }
                                      );
                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `Receipt_${selectedInvoice.invoice_number.replace(/\//g, '_')}_${index + 1}.pdf`;
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      } else {
                                        // Parse error response properly
                                        const errorData = await response.json().catch(() => ({}));
                                        alert(errorData.detail || 'Failed to download receipt');
                                      }
                                    } catch (error) {
                                      console.error('Error downloading receipt:', error);
                                      alert('Failed to download receipt');
                                    }
                                  }}
                                  className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                                  title="Download Receipt"
                                >
                                  <Download size={16} />
                                </button>
                                <button
                                  onClick={() => handleSendPaymentReceipt(selectedInvoice.id, selectedInvoice.invoice_number, index)}
                                  className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                  title={record.receipt_sent ? "Re-Send Receipt" : "Send Receipt"}
                                >
                                  <Send size={16} />
                                </button>
                              </>
                            )}
                            {/* Cancel button - Admin/SuperAdmin, only for active payments */}
                            {record.status !== 'cancelled' && (admin?.role === 'admin' || admin?.role === 'super_admin') && (
                              <button
                                onClick={() => openCancelPaymentModal(selectedInvoice.id, index, record.amount)}
                                disabled={cancellingPayment === `${selectedInvoice.id}-${index}`}
                                className="p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded disabled:opacity-50"
                                title="Cancel Payment"
                              >
                                {cancellingPayment === `${selectedInvoice.id}-${index}` ? (
                                  <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                  <Ban size={16} />
                                )}
                              </button>
                            )}
                            {/* Delete button - SuperAdmin only (shown for all payments) */}
                            {admin?.role === 'super_admin' && (
                              <button
                                onClick={() => handleDeletePayment(selectedInvoice.id, index, record.amount)}
                                disabled={deletingPayment === `${selectedInvoice.id}-${index}`}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Delete Payment Permanently"
                              >
                                {deletingPayment === `${selectedInvoice.id}-${index}` ? (
                                  <RefreshCw size={16} className="animate-spin" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Record Payment Form */}
              {selectedInvoice.payment_status !== 'paid' && (
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Record Payment</h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount (₹) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={paymentData.amount}
                          onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Date *
                        </label>
                        <input
                          type="date"
                          value={paymentData.payment_date}
                          onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Method *
                      </label>
                      <select
                        value={paymentData.payment_method}
                        onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="UPI">UPI</option>
                        <option value="Cash">Cash</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Card">Card</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transaction Reference
                      </label>
                      <input
                        type="text"
                        value={paymentData.transaction_reference}
                        onChange={(e) => setPaymentData({ ...paymentData, transaction_reference: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Transaction ID / Cheque Number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Screenshot(s)
                      </label>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/jpg,.pdf"
                        onChange={(e) => setPaymentScreenshots(Array.from(e.target.files))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {paymentScreenshots.length > 0 && (
                        <p className="text-sm text-green-600 mt-1">
                          ✓ {paymentScreenshots.length} file(s) selected
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={paymentData.notes}
                        onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Additional payment notes..."
                      />
                    </div>

                    <button
                      onClick={handleConfirmPayment}
                      disabled={submittingPayment}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <IndianRupee size={20} />
                      {submittingPayment ? 'Processing...' : 'Confirm Payment'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Invoice Modal */}
      {showCancelModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-red-600">
                Cancel Invoice
              </h2>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedInvoice(null);
                  setCancelData({ reason: '', notes: '' });
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Cancelling this invoice will:
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>Mark the invoice as cancelled</li>
                  <li>Void any pending partner commissions</li>
                  <li>Create reversal entries for paid commissions</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Invoice Number</p>
                <p className="font-semibold text-gray-900">{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-gray-600 mt-2">Lead</p>
                <p className="font-semibold text-gray-900">{selectedInvoice.lead_name} ({selectedInvoice.lead_id})</p>
                <p className="text-sm text-gray-600 mt-2">Amount</p>
                <p className="font-semibold text-gray-900">₹{selectedInvoice.total_amount?.toFixed(2)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cancellation Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={cancelData.reason}
                  onChange={(e) => setCancelData({ ...cancelData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select a reason...</option>
                  <option value="Client requested cancellation">Client requested cancellation</option>
                  <option value="Duplicate invoice">Duplicate invoice</option>
                  <option value="Incorrect amount">Incorrect amount</option>
                  <option value="Service not provided">Service not provided</option>
                  <option value="Refund processed">Refund processed</option>
                  <option value="Agreement cancelled">Agreement cancelled</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={cancelData.notes}
                  onChange={(e) => setCancelData({ ...cancelData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Any additional details about the cancellation..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedInvoice(null);
                    setCancelData({ reason: '', notes: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Keep Invoice
                </button>
                <button
                  onClick={handleCancelInvoice}
                  disabled={cancellingInvoice || !cancelData.reason}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <XCircle size={18} />
                  {cancellingInvoice ? 'Cancelling...' : 'Cancel Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-blue-600">
                Create Refund
              </h2>
              <button
                onClick={() => {
                  setShowRefundModal(false);
                  setSelectedInvoice(null);
                  setRefundData({ refund_type: 'full', refund_amount: '', refund_method: 'Bank Transfer', refund_reference: '', notes: '' });
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Creating a refund will impact partner commissions:
                </p>
                <ul className="text-sm text-blue-700 mt-2 list-disc list-inside">
                  <li>Pending commissions will be voided or reduced</li>
                  <li>Paid commissions will create a reversal for recovery</li>
                  <li>Audit trail will be added to partner records</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Invoice Number</p>
                <p className="font-semibold text-gray-900">{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-gray-600 mt-2">Lead</p>
                <p className="font-semibold text-gray-900">{selectedInvoice.lead_name} ({selectedInvoice.lead_id})</p>
                <p className="text-sm text-gray-600 mt-2">Amount Paid (Available for Refund)</p>
                <p className="font-semibold text-green-600 text-lg">₹{selectedInvoice.amount_paid?.toFixed(2)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="refund_type"
                      value="full"
                      checked={refundData.refund_type === 'full'}
                      onChange={(e) => setRefundData({ ...refundData, refund_type: e.target.value })}
                      className="mr-2"
                    />
                    Full Refund (₹{selectedInvoice.amount_paid?.toFixed(2)})
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="refund_type"
                      value="partial"
                      checked={refundData.refund_type === 'partial'}
                      onChange={(e) => setRefundData({ ...refundData, refund_type: e.target.value })}
                      className="mr-2"
                    />
                    Partial Refund
                  </label>
                </div>
              </div>

              {refundData.refund_type === 'partial' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Refund Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      value={refundData.refund_amount}
                      onChange={(e) => setRefundData({ ...refundData, refund_amount: e.target.value })}
                      max={selectedInvoice.amount_paid}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter refund amount"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Maximum: ₹{selectedInvoice.amount_paid?.toFixed(2)}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Refund Method
                </label>
                <select
                  value={refundData.refund_method}
                  onChange={(e) => setRefundData({ ...refundData, refund_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="Credit Note">Credit Note</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transaction Reference
                </label>
                <input
                  type="text"
                  value={refundData.refund_reference}
                  onChange={(e) => setRefundData({ ...refundData, refund_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter transaction reference (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={refundData.notes}
                  onChange={(e) => setRefundData({ ...refundData, notes: e.target.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Additional notes about the refund..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowRefundModal(false);
                    setSelectedInvoice(null);
                    setRefundData({ refund_type: 'full', refund_amount: '', refund_method: 'Bank Transfer', refund_reference: '', notes: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRefund}
                  disabled={processingRefund || (refundData.refund_type === 'partial' && !refundData.refund_amount)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <RotateCcw size={18} />
                  {processingRefund ? 'Processing...' : 'Process Refund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recall Invoice Modal */}
      {showRecallModal && recallInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-amber-50">
              <h2 className="text-lg font-bold text-amber-700 flex items-center gap-2">
                <Undo2 size={20} />
                Recall Invoice
              </h2>
              <button
                onClick={() => {
                  setShowRecallModal(false);
                  setRecallInvoice(null);
                  setRecallNotes('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> You are about to recall (undo cancellation) this invoice. 
                  The invoice will be restored to its previous status based on payment records.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Invoice Number:</span>
                  <span className="font-semibold text-gray-900">{recallInvoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Lead:</span>
                  <span className="font-semibold text-gray-900">{recallInvoice.lead_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-semibold text-green-600">₹{Math.round(recallInvoice.amount_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cancelled By:</span>
                  <span className="text-gray-900">{recallInvoice.cancellation?.cancelled_by || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Cancellation Reason:</span>
                  <span className="text-gray-900 max-w-[200px] truncate" title={recallInvoice.cancellation?.cancellation_reason}>
                    {recallInvoice.cancellation?.cancellation_reason || 'N/A'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recall Notes (Optional)
                </label>
                <textarea
                  value={recallNotes}
                  onChange={(e) => setRecallNotes(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder="Reason for recalling this invoice..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowRecallModal(false);
                    setRecallInvoice(null);
                    setRecallNotes('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecallInvoice}
                  disabled={recallingInvoice === recallInvoice.id}
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Undo2 size={18} />
                  {recallingInvoice === recallInvoice.id ? 'Recalling...' : 'Recall Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Payment Modal */}
      {showCancelPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-amber-50">
              <h2 className="text-lg font-bold text-amber-700 flex items-center gap-2">
                <Ban size={20} />
                Cancel Payment
              </h2>
              <button
                onClick={() => {
                  setShowCancelPaymentModal(false);
                  setCancelPaymentInvoiceId(null);
                  setCancelPaymentIndex(null);
                  setCancelPaymentReason('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> Cancelling this payment will update the invoice balance. 
                  The payment record will be marked as cancelled but not deleted.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span className="font-semibold text-gray-900">₹{Math.round(cancelPaymentAmount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Invoice:</span>
                  <span className="font-semibold text-gray-900">{selectedInvoice?.invoice_number || 'N/A'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Cancellation <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelPaymentReason}
                  onChange={(e) => setCancelPaymentReason(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  placeholder="Enter reason for cancelling this payment..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowCancelPaymentModal(false);
                    setCancelPaymentInvoiceId(null);
                    setCancelPaymentIndex(null);
                    setCancelPaymentReason('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={handleCancelPayment}
                  disabled={!cancelPaymentReason.trim() || cancellingPayment !== null}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Ban size={18} />
                  {cancellingPayment !== null ? 'Cancelling...' : 'Cancel Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
